import { DEFAULT_FONT } from '@/constants';
import { stripTrailingEmptyParagraphs } from '@/lib/rich-text';
import {
    BLOCK_TEXT_SPACING_RESET,
    REPORT_CONTENT_CLASSNAME,
    REPORT_CONTENT_WRAPPER_CLASSNAME,
} from '../constants';
import { CONTENT_WIDTH } from './constants';
import type { HtmlSplitResult } from './types';

interface SplitHtmlOptions {
    containerWidth?: number;
    fontConfig?: {
        family?: string;
    };
    allowOversizeMediaScaling?: boolean;
}

interface SplitNodeResult {
    fittedNode: Node | null;
    remainingNode: Node | null;
    fittedHeight: number;
    pending: boolean;
}

interface MeasurementResult {
    height: number;
    pending: boolean;
}

interface AppendedMeasurementResult extends MeasurementResult {
    candidate: Node;
}

interface MeasurementContainer {
    root: HTMLDivElement;
    content: HTMLDivElement;
}

interface TextNodeBoundary {
    node: Text;
    start: number;
    end: number;
}

export function splitHtmlContent(
    rawHtmlContent: string,
    maxHeight: number,
    {
        containerWidth = CONTENT_WIDTH,
        fontConfig = {},
        allowOversizeMediaScaling = false,
    }: SplitHtmlOptions = {},
): HtmlSplitResult {
    // Keep measurement in sync with EditableSection, which strips trailing
    // empty paragraphs before rendering.
    const htmlContent = stripTrailingEmptyParagraphs(rawHtmlContent);

    if (!htmlContent || maxHeight <= 0) {
        return {
            fitted: '',
            remaining: htmlContent || '',
            fittedHeight: 0,
            remainingHeight: 0,
            pending: false,
        };
    }

    const container = createMeasurementContainer(containerWidth, fontConfig.family || DEFAULT_FONT);

    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlContent, 'text/html');
        const nodes = Array.from(doc.body.childNodes);

        const fittedNodes: Node[] = [];
        const remainingNodes: Node[] = [];
        let currentHeight = 0;
        let splitOccurred = false;
        let pending = false;

        for (const node of nodes) {
            if (splitOccurred) {
                remainingNodes.push(node);
                continue;
            }

            const wholeMeasurement = appendCandidate(container, node);

            if (wholeMeasurement.pending) {
                pending = true;
                container.content.removeChild(wholeMeasurement.candidate);
                remainingNodes.push(node);
                splitOccurred = true;
                continue;
            }

            if (wholeMeasurement.height <= maxHeight) {
                fittedNodes.push(node);
                currentHeight = wholeMeasurement.height;
                continue;
            }

            container.content.removeChild(wholeMeasurement.candidate);

            const splitNodeResult = splitNode({
                node,
                container,
                maxHeight,
                allowOversizeMediaScaling,
            });

            if (splitNodeResult.pending) {
                pending = true;
            }

            if (splitNodeResult.fittedNode) {
                fittedNodes.push(splitNodeResult.fittedNode);
                currentHeight = splitNodeResult.fittedHeight;
            }

            if (splitNodeResult.remainingNode) {
                remainingNodes.push(splitNodeResult.remainingNode);
            } else if (!splitNodeResult.fittedNode) {
                remainingNodes.push(node);
            }

            splitOccurred = true;
        }

        // Keep-with-next: never leave a heading stranded as the last element of
        // a page with its content on the next.
        if (remainingNodes.length > 0) {
            const strandedTail = takeTrailingHeading(fittedNodes);
            if (strandedTail) {
                remainingNodes.unshift(...strandedTail);
                currentHeight = measureNodes(container, fittedNodes).height;
            }
        }

        const remainingHeight = measureNodes(container, remainingNodes).height;

        return {
            fitted: serializeNodes(fittedNodes),
            remaining: serializeNodes(remainingNodes),
            fittedHeight: currentHeight,
            remainingHeight,
            pending,
        };
    } finally {
        container.root.remove();
    }
}

function createMeasurementContainer(
    containerWidth: number,
    fontFamily: string,
): MeasurementContainer {
    const root = document.createElement('div');
    root.style.width = `${containerWidth}px`;
    root.style.position = 'absolute';
    root.style.visibility = 'hidden';
    root.style.pointerEvents = 'none';
    root.style.left = '-9999px';
    root.style.top = '-9999px';
    root.style.fontFamily = fontFamily;

    // Mirror the real page's box model: TestSectionRenderer wraps blocks in a
    // container carrying BLOCK_TEXT_SPACING_RESET (zeroes the first/last prose
    // margins). Without it, fragment heights measured here systematically
    // disagree with the live render and text clips at page bottoms.
    const wrapper = document.createElement('div');
    wrapper.className = `${REPORT_CONTENT_WRAPPER_CLASSNAME} ${BLOCK_TEXT_SPACING_RESET}`;

    const content = document.createElement('div');
    content.className = REPORT_CONTENT_CLASSNAME;

    wrapper.appendChild(content);
    root.appendChild(wrapper);
    document.body.appendChild(root);

    return { root, content };
}

function measureHeight(container: MeasurementContainer) {
    return Math.ceil(container.content.getBoundingClientRect().height);
}

function serializeNodes(nodes: Node[]) {
    const div = document.createElement('div');
    nodes.forEach((node) => div.appendChild(node.cloneNode(true)));
    return div.innerHTML;
}

function measureNodes(container: MeasurementContainer, nodes: Node[]): MeasurementResult {
    container.content.innerHTML = '';

    nodes.forEach((node) => {
        const candidate = node.cloneNode(true);
        prepareImages(candidate);
        container.content.appendChild(candidate);
    });

    return {
        height: measureHeight(container),
        pending: hasPendingImages(container.content),
    };
}

function measureCandidate(
    container: MeasurementContainer,
    node: Node,
    maxMediaHeight?: number,
): MeasurementResult {
    const measurement = appendCandidate(container, node, maxMediaHeight);
    container.content.removeChild(measurement.candidate);

    return measurement;
}

function appendCandidate(
    container: MeasurementContainer,
    node: Node,
    maxMediaHeight?: number,
): AppendedMeasurementResult {
    const candidate = node.cloneNode(true);
    prepareImages(candidate, maxMediaHeight);
    container.content.appendChild(candidate);

    return {
        candidate,
        height: measureHeight(container),
        pending: hasPendingImages(candidate),
    };
}

function splitNode({
    node,
    container,
    maxHeight,
    allowOversizeMediaScaling,
}: {
    node: Node;
    container: MeasurementContainer;
    maxHeight: number;
    allowOversizeMediaScaling: boolean;
}): SplitNodeResult {
    if (node.nodeType === Node.TEXT_NODE) {
        return splitTextLikeNode(node, container, maxHeight);
    }

    if (!isElement(node)) {
        return {
            fittedNode: null,
            remainingNode: null,
            fittedHeight: 0,
            pending: false,
        };
    }

    const tagName = node.tagName.toUpperCase();

    if (/^H[1-6]$/.test(tagName)) {
        // Headings never split mid-text — the whole heading moves to the next page.
        return {
            fittedNode: null,
            remainingNode: null,
            fittedHeight: 0,
            pending: false,
        };
    }

    if (tagName === 'P' || tagName === 'BLOCKQUOTE') {
        return splitTextLikeNode(node, container, maxHeight);
    }

    if (tagName === 'UL' || tagName === 'OL') {
        return splitListNode(node, container, maxHeight);
    }

    if (tagName === 'TABLE') {
        if (!allowOversizeMediaScaling) {
            // Treat table as atomic — move whole table to next page
            return {
                fittedNode: null,
                remainingNode: null,
                fittedHeight: 0,
                pending: false,
            };
        }
        // Fresh page: table is taller than a full page — split by rows
        return splitTableNode(node as HTMLTableElement, container, maxHeight);
    }

    if (tagName === 'DIV') {
        const hasBlockChildren = !!(node as HTMLElement).querySelector(
            'table, ul, ol, blockquote, div',
        );
        if (hasBlockChildren) {
            return splitContainerNode(
                node as HTMLElement,
                container,
                maxHeight,
                allowOversizeMediaScaling,
            );
        }
        return splitTextLikeNode(node, container, maxHeight);
    }

    if (
        allowOversizeMediaScaling &&
        (tagName === 'IMG' || tagName === 'FIGURE' || tagName === 'PICTURE')
    ) {
        const scaledMeasurement = measureCandidate(container, node, maxHeight);

        if (!scaledMeasurement.pending && scaledMeasurement.height <= maxHeight) {
            const fittedNode = node.cloneNode(true);
            prepareImages(fittedNode, maxHeight);

            return {
                fittedNode,
                remainingNode: null,
                fittedHeight: scaledMeasurement.height,
                pending: false,
            };
        }

        return {
            fittedNode: null,
            remainingNode: null,
            fittedHeight: 0,
            pending: scaledMeasurement.pending,
        };
    }

    return {
        fittedNode: null,
        remainingNode: null,
        fittedHeight: 0,
        pending: false,
    };
}

function splitContainerNode(
    node: HTMLElement,
    container: MeasurementContainer,
    maxHeight: number,
    allowOversizeMediaScaling: boolean,
): SplitNodeResult {
    const children = Array.from(node.childNodes);

    if (children.length === 0) {
        return {
            fittedNode: null,
            remainingNode: null,
            fittedHeight: 0,
            pending: false,
        };
    }

    const fittedChildren: Node[] = [];
    const remainingChildren: Node[] = [];
    let splitOccurred = false;
    let currentHeight = 0;
    let pending = false;
    const initialChildCount = container.content.childNodes.length;

    for (const child of children) {
        if (splitOccurred) {
            remainingChildren.push(child);
            continue;
        }

        const wholeMeasurement = appendCandidate(container, child);

        if (wholeMeasurement.pending) {
            pending = true;
            container.content.removeChild(wholeMeasurement.candidate);
            remainingChildren.push(child);
            splitOccurred = true;
            continue;
        }

        if (wholeMeasurement.height <= maxHeight) {
            // Candidate stays in container for cumulative measurement
            fittedChildren.push(child);
            currentHeight = wholeMeasurement.height;
            continue;
        }

        container.content.removeChild(wholeMeasurement.candidate);

        const splitResult = splitNode({
            node: child,
            container,
            maxHeight,
            allowOversizeMediaScaling,
        });

        if (splitResult.pending) pending = true;

        if (splitResult.fittedNode) {
            fittedChildren.push(splitResult.fittedNode);
            currentHeight = splitResult.fittedHeight;
        }

        if (splitResult.remainingNode) {
            remainingChildren.push(splitResult.remainingNode);
        } else if (!splitResult.fittedNode) {
            remainingChildren.push(child);
        }

        splitOccurred = true;
    }

    // Keep-with-next: never strand a heading as the last fitted child. Children
    // that fit whole are the candidates still appended (split fragments and
    // failed children are never left in the container), so drop the stranded
    // tail's candidates and re-read the cumulative height.
    if (remainingChildren.length > 0) {
        const strandedTail = takeTrailingHeading(fittedChildren);
        if (strandedTail) {
            remainingChildren.unshift(...strandedTail);
            strandedTail.forEach(() => {
                if (container.content.childNodes.length > initialChildCount) {
                    container.content.removeChild(container.content.lastChild!);
                }
            });
            currentHeight = measureHeight(container);
        }
    }

    // Restore container to pre-call state
    while (container.content.childNodes.length > initialChildCount) {
        container.content.removeChild(container.content.lastChild!);
    }

    if (fittedChildren.length === 0) {
        return { fittedNode: null, remainingNode: null, fittedHeight: 0, pending };
    }

    const fittedNode = node.cloneNode(false) as HTMLElement;
    fittedChildren.forEach((n) => fittedNode.appendChild(n.cloneNode(true)));

    const remainingNode =
        remainingChildren.length > 0
            ? (() => {
                  const clone = node.cloneNode(false) as HTMLElement;
                  remainingChildren.forEach((n) => clone.appendChild(n.cloneNode(true)));
                  return clone;
              })()
            : null;

    return { fittedNode, remainingNode, fittedHeight: currentHeight, pending };
}

// Widow/orphan control: a split must leave at least this many text lines on
// each side, and a node shorter than twice that is never split at all.
const MIN_FRAGMENT_LINES = 2;

const NO_FIT: SplitNodeResult = {
    fittedNode: null,
    remainingNode: null,
    fittedHeight: 0,
    pending: false,
};

function splitTextLikeNode(
    node: Node,
    container: MeasurementContainer,
    maxHeight: number,
): SplitNodeResult {
    const boundaries = getWordBoundaries(node);

    if (boundaries.length === 0) {
        return NO_FIT;
    }

    // Measurements are cumulative (earlier fitted nodes stay appended), so a
    // fragment's own height is its measurement minus this base.
    const baseHeight = measureHeight(container);
    const lineHeight = resolveLineHeight(node, container);
    const totalLength = getNodeTextLength(node);

    // A node shorter than 2× the minimum fragment can't split without creating
    // a widow or an orphan — move it whole to the next page.
    if (lineHeight > 0) {
        const wholeMeasurement = measureCandidate(container, node);
        if (
            !wholeMeasurement.pending &&
            wholeMeasurement.height - baseHeight < 2 * MIN_FRAGMENT_LINES * lineHeight
        ) {
            return NO_FIT;
        }
    }

    let left = 0;
    let right = boundaries.length - 1;
    let bestIndex = -1;
    let bestHeight = 0;
    let pending = false;

    while (left <= right) {
        const middle = Math.floor((left + right) / 2);
        const fittedNode = createTextSegment(node, 0, boundaries[middle]);

        if (!fittedNode) {
            right = middle - 1;
            continue;
        }

        const measurement = measureCandidate(container, fittedNode);

        if (measurement.pending) {
            pending = true;
            break;
        }

        if (measurement.height <= maxHeight) {
            bestIndex = middle;
            bestHeight = measurement.height;
            left = middle + 1;
        } else {
            right = middle - 1;
        }
    }

    if (bestIndex < 0) {
        return { ...NO_FIT, pending };
    }

    if (lineHeight > 0) {
        // Orphan rule: the fitted fragment must hold at least MIN_FRAGMENT_LINES.
        if (bestHeight - baseHeight < MIN_FRAGMENT_LINES * lineHeight) {
            return { ...NO_FIT, pending };
        }

        // Widow rule: the continuation must too — back the split point off until
        // it does, re-verifying the shrunken fitted fragment on each step.
        while (bestIndex >= 0) {
            const remainingProbe = createTextSegment(node, boundaries[bestIndex], totalLength);
            if (!remainingProbe) break;

            const remainingMeasurement = measureCandidate(container, remainingProbe);
            if (remainingMeasurement.pending) {
                pending = true;
                break;
            }
            if (remainingMeasurement.height - baseHeight >= MIN_FRAGMENT_LINES * lineHeight) {
                break;
            }

            bestIndex -= 1;
            if (bestIndex < 0) break;

            const reducedFitted = createTextSegment(node, 0, boundaries[bestIndex]);
            if (!reducedFitted) {
                bestIndex = -1;
                break;
            }

            const reducedMeasurement = measureCandidate(container, reducedFitted);
            if (reducedMeasurement.pending) {
                pending = true;
                bestIndex = -1;
                break;
            }
            if (
                reducedMeasurement.height > maxHeight ||
                reducedMeasurement.height - baseHeight < MIN_FRAGMENT_LINES * lineHeight
            ) {
                bestIndex = -1;
                break;
            }
            bestHeight = reducedMeasurement.height;
        }

        if (bestIndex < 0) {
            return { ...NO_FIT, pending };
        }
    }

    const fittedNode = createTextSegment(node, 0, boundaries[bestIndex]);
    const remainingNode = createTextSegment(node, boundaries[bestIndex], totalLength);

    return {
        fittedNode,
        remainingNode,
        fittedHeight: bestHeight,
        pending,
    };
}

// Resolve the rendered line-height of a text-like node inside the measurement
// container. Returns 0 when it can't be determined (widow/orphan rules are then
// skipped rather than guessed).
function resolveLineHeight(node: Node, container: MeasurementContainer): number {
    const probe = isElement(node)
        ? (node.cloneNode(false) as HTMLElement)
        : document.createElement('span');
    probe.textContent = 'x';
    container.content.appendChild(probe);

    try {
        const computed = Number.parseFloat(getComputedStyle(probe).lineHeight);
        if (Number.isFinite(computed) && computed > 0) return computed;

        const rectHeight = probe.getBoundingClientRect().height;
        return rectHeight > 0 ? rectHeight : 0;
    } finally {
        container.content.removeChild(probe);
    }
}

function splitListNode(
    node: HTMLElement,
    container: MeasurementContainer,
    maxHeight: number,
): SplitNodeResult {
    const items = Array.from(node.children).filter((child) => child.tagName.toUpperCase() === 'LI');

    if (items.length === 0) {
        return {
            fittedNode: null,
            remainingNode: null,
            fittedHeight: 0,
            pending: false,
        };
    }

    let left = 1;
    let right = items.length;
    let bestCount = 0;
    let bestHeight = 0;
    let pending = false;

    while (left <= right) {
        const middle = Math.floor((left + right) / 2);
        const fittedNode = createListSegment(node, items, 0, middle);
        const measurement = measureCandidate(container, fittedNode);

        if (measurement.pending) {
            pending = true;
            break;
        }

        if (measurement.height <= maxHeight) {
            bestCount = middle;
            bestHeight = measurement.height;
            left = middle + 1;
        } else {
            right = middle - 1;
        }
    }

    if (bestCount === 0) {
        return {
            fittedNode: null,
            remainingNode: null,
            fittedHeight: 0,
            pending,
        };
    }

    const fittedNode = createListSegment(node, items, 0, bestCount);
    const remainingNode =
        bestCount < items.length ? createListSegment(node, items, bestCount, items.length) : null;

    return {
        fittedNode,
        remainingNode,
        fittedHeight: bestHeight,
        pending,
    };
}

function splitTableNode(
    node: HTMLTableElement,
    container: MeasurementContainer,
    maxHeight: number,
): SplitNodeResult {
    const bodyRows = getBodyRows(node);

    if (bodyRows.length === 0) {
        return {
            fittedNode: null,
            remainingNode: null,
            fittedHeight: 0,
            pending: false,
        };
    }

    let left = 1;
    let right = bodyRows.length;
    let bestCount = 0;
    let bestHeight = 0;
    let pending = false;

    while (left <= right) {
        const middle = Math.floor((left + right) / 2);
        const fittedNode = createTableSegment(node, bodyRows, 0, middle);
        const measurement = measureCandidate(container, fittedNode);

        if (measurement.pending) {
            pending = true;
            break;
        }

        if (measurement.height <= maxHeight) {
            bestCount = middle;
            bestHeight = measurement.height;
            left = middle + 1;
        } else {
            right = middle - 1;
        }
    }

    if (bestCount === 0) {
        return {
            fittedNode: null,
            remainingNode: null,
            fittedHeight: 0,
            pending,
        };
    }

    const fittedNode = createTableSegment(node, bodyRows, 0, bestCount);
    const remainingNode =
        bestCount < bodyRows.length
            ? createTableSegment(node, bodyRows, bestCount, bodyRows.length)
            : null;

    return {
        fittedNode,
        remainingNode,
        fittedHeight: bestHeight,
        pending,
    };
}

function createListSegment(
    node: HTMLElement,
    items: Element[],
    startIndex: number,
    endIndex: number,
) {
    const clone = node.cloneNode(false) as HTMLElement;
    items.slice(startIndex, endIndex).forEach((item) => {
        clone.appendChild(item.cloneNode(true));
    });

    if (clone.tagName.toUpperCase() === 'OL' && startIndex > 0) {
        const originalStart = Number.parseInt(node.getAttribute('start') || '1', 10);
        clone.setAttribute('start', String(originalStart + startIndex));
    }

    return clone;
}

function createTableSegment(
    node: HTMLTableElement,
    bodyRows: HTMLTableRowElement[],
    startIndex: number,
    endIndex: number,
) {
    const clone = node.cloneNode(false) as HTMLTableElement;
    clone.innerHTML = '';

    Array.from(node.children).forEach((child) => {
        const tagName = child.tagName.toUpperCase();
        if (tagName === 'CAPTION' || tagName === 'COLGROUP') {
            clone.appendChild(child.cloneNode(true));
        }
    });

    if (node.tHead && node.tHead.rows.length > 0) {
        const head = node.tHead.cloneNode(false) as HTMLTableSectionElement;
        Array.from(node.tHead.rows).forEach((row) => {
            head.appendChild(row.cloneNode(true));
        });
        clone.appendChild(head);
    }

    const body = document.createElement('tbody');
    bodyRows.slice(startIndex, endIndex).forEach((row) => {
        body.appendChild(row.cloneNode(true));
    });
    clone.appendChild(body);

    return clone;
}

function getBodyRows(node: HTMLTableElement) {
    if (node.tBodies.length > 0) {
        return Array.from(node.tBodies).flatMap((body) => Array.from(body.rows));
    }

    const headerRows = node.tHead ? Array.from(node.tHead.rows) : [];
    return Array.from(node.rows).filter((row) => !headerRows.includes(row));
}

function getWordBoundaries(node: Node) {
    const text = node.textContent || '';
    const boundaries: number[] = [];
    const regex = /\S+\s*/g;
    let match: RegExpExecArray | null = null;

    while ((match = regex.exec(text)) !== null) {
        boundaries.push(match.index + match[0].length);
    }

    return boundaries;
}

function createTextSegment(node: Node, startOffset: number, endOffset: number) {
    if (startOffset >= endOffset) return null;

    if (node.nodeType === Node.TEXT_NODE) {
        return document.createTextNode((node.textContent || '').slice(startOffset, endOffset));
    }

    if (!isElement(node)) return null;

    const boundaries = collectTextNodeBoundaries(node);
    const startBoundary = resolveTextBoundary(boundaries, startOffset);
    // A tail segment keeps the Range end at the node's content end (from
    // selectNodeContents) instead of the last text node, so trailing non-text
    // siblings — e.g. <br> runs after the final word — survive the split.
    const isTailSegment = endOffset >= getNodeTextLength(node);
    const endBoundary = isTailSegment ? null : resolveTextBoundary(boundaries, endOffset);

    if (!startBoundary || (!isTailSegment && !endBoundary)) return null;

    const range = node.ownerDocument.createRange();
    range.selectNodeContents(node);
    range.setStart(startBoundary.node, startBoundary.offset);
    if (endBoundary) {
        range.setEnd(endBoundary.node, endBoundary.offset);
    }

    const clone = node.cloneNode(false);
    clone.appendChild(range.cloneContents());

    return pruneEmptyNodes(clone);
}

function collectTextNodeBoundaries(node: Node) {
    const boundaries: TextNodeBoundary[] = [];
    const ownerDocument = node.ownerDocument || document;
    const walker = ownerDocument.createTreeWalker(node, NodeFilter.SHOW_TEXT);

    let current = walker.nextNode();
    let offset = 0;

    while (current) {
        const textNode = current as Text;
        const length = textNode.textContent?.length || 0;

        if (length > 0) {
            boundaries.push({
                node: textNode,
                start: offset,
                end: offset + length,
            });
            offset += length;
        }

        current = walker.nextNode();
    }

    return boundaries;
}

function resolveTextBoundary(
    boundaries: TextNodeBoundary[],
    offset: number,
): {
    node: Text;
    offset: number;
} | null {
    if (boundaries.length === 0) return null;

    if (offset <= 0) {
        return {
            node: boundaries[0].node,
            offset: 0,
        };
    }

    for (const boundary of boundaries) {
        if (offset <= boundary.end) {
            return {
                node: boundary.node,
                offset: Math.max(0, offset - boundary.start),
            };
        }
    }

    const lastBoundary = boundaries[boundaries.length - 1];

    return {
        node: lastBoundary.node,
        offset: lastBoundary.end - lastBoundary.start,
    };
}

function getNodeTextLength(node: Node) {
    return (node.textContent || '').length;
}

function pruneEmptyNodes(node: Node | null): Node | null {
    if (!node) return null;

    if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent?.length ? node : null;
    }

    if (!isElement(node)) return node;

    // Void elements are meaningful despite having no text or descendants —
    // the hasMedia check below only sees descendants, not the node itself.
    if (/^(BR|IMG|HR)$/.test(node.tagName.toUpperCase())) return node;

    Array.from(node.childNodes).forEach((child) => {
        const pruned = pruneEmptyNodes(child);

        if (!pruned) {
            child.remove();
        }
    });

    const hasText = (node.textContent || '').trim().length > 0;
    const hasElementChild = Array.from(node.children).length > 0;
    const hasMedia = node.querySelector('img,table,ul,ol,br');

    if (!hasText && !hasElementChild && !hasMedia) {
        return null;
    }

    return node;
}

function isElement(node: Node): node is HTMLElement {
    return node.nodeType === Node.ELEMENT_NODE;
}

function isHeadingNode(node: Node): boolean {
    return isElement(node) && /^H[1-6]$/.test(node.tagName.toUpperCase());
}

// If the last meaningful entry of `nodes` is a heading, splice off and return
// the tail from that heading onward (including any trailing whitespace-only
// text nodes that would otherwise hide it); null when there is nothing to move.
function takeTrailingHeading(nodes: Node[]): Node[] | null {
    let index = nodes.length - 1;
    while (
        index >= 0 &&
        nodes[index].nodeType === Node.TEXT_NODE &&
        !(nodes[index].textContent || '').trim()
    ) {
        index -= 1;
    }

    if (index < 0 || !isHeadingNode(nodes[index])) return null;

    return nodes.splice(index);
}

function getImages(node: Node) {
    if (node instanceof HTMLImageElement) {
        return [node];
    }

    if (!(node instanceof Element)) return [];

    return Array.from(node.querySelectorAll('img'));
}

function prepareImages(node: Node, maxHeight?: number) {
    getImages(node).forEach((image) => {
        image.style.maxWidth = '100%';
        image.style.height = 'auto';
        image.style.display = image.style.display || 'block';

        if (maxHeight) {
            image.style.maxHeight = `${Math.max(maxHeight, 0)}px`;
            image.style.width = 'auto';
            image.style.objectFit = 'contain';
        }
    });
}

function hasPendingImages(node: Node) {
    return getImages(node).some((image) => {
        if (image.complete && image.naturalHeight > 0) return false;

        return !image.getAttribute('height') && !image.style.height;
    });
}
